(() => {
  if (window.freezketchWorldLoaded) return;
  window.freezketchWorldLoaded = true;

  const atlasCache = new Map();

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
  }

  function fetchJson(url) {
    if (url?.startsWith('#')) {
      const source = document.querySelector(url);
      if (!source) return Promise.reject(new Error(`Unable to load ${url}`));
      return Promise.resolve(JSON.parse(source.textContent));
    }

    return fetch(url).then((response) => {
      if (!response.ok) throw new Error(`Unable to load ${url}`);
      return response.json();
    });
  }

  async function getAtlas(url) {
    if (!url) return { entries: [] };
    if (!atlasCache.has(url)) atlasCache.set(url, fetchJson(url));
    return atlasCache.get(url);
  }

  function entryLookup(atlas) {
    return new Map((atlas.entries || []).map((entry) => [entry.id, entry]));
  }

  function imageMarkup(url, label) {
    if (!url) return `<div class="fk-world__placeholder">${escapeHtml(label)}</div>`;
    return `<img src="${escapeHtml(url)}" alt="${escapeHtml(label)}" loading="lazy">`;
  }

  function mediaMarkup(item, label) {
    if (item?.video) {
      const poster = item.image ? ` poster="${escapeHtml(item.image)}"` : '';
      return `<video autoplay muted loop playsinline${poster}><source src="${escapeHtml(item.video)}" type="video/mp4"></video>`;
    }

    return imageMarkup(item?.image, label);
  }

  function cardMediaMarkup(item) {
    const label = item.id || item.title || 'Freezketch entry';

    if (item?.video) {
      const poster = item.image ? ` poster="${escapeHtml(item.image)}"` : '';
      return `
        <div class="fk-world__card-image">
          <video autoplay muted loop playsinline${poster}>
            <source src="${escapeHtml(item.video)}" type="video/mp4">
          </video>
        </div>
      `;
    }

    if (item?.image) {
      return `
        <div class="fk-world__card-image">
          <img src="${escapeHtml(item.image)}" alt="${escapeHtml(label)} preview" loading="lazy">
        </div>
      `;
    }

    return '';
  }

  function heroMedia(hero) {
    const label = hero?.title || 'Freezketch Atlas';
    return mediaMarkup(hero, label);
  }

  function button(url, label, secondary = false) {
    if (!url || !label) return '';
    return `<a class="fk-world__button${secondary ? ' fk-world__button--secondary' : ''}" href="${escapeHtml(url)}">${escapeHtml(label)}</a>`;
  }

  function renderEntryCard(item) {
    const url = item.url || item.atlas_url || '#';
    const image = cardMediaMarkup(item);

    return `
      <a class="fk-world__card" href="${escapeHtml(url)}">
        ${image}
        <span>
          <p class="fk-world__card-meta">${escapeHtml(item.id || item.count || item.status || '')}</p>
          <h3 class="fk-world__card-title">${escapeHtml(item.title)}</h3>
          ${item.meta ? `<p class="fk-world__card-note">${escapeHtml(item.meta)}</p>` : ''}
        </span>
        <span class="fk-world__card-meta">Open</span>
      </a>
    `;
  }

  function renderGiftList(items) {
    return `<ul>${(items || []).map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
  }

  function renderGiftPath(path, index) {
    const number = String(index + 1).padStart(2, '0');

    return `
      <details class="fk-world__gift-path">
        <summary>
          <span class="fk-world__gift-number">${number}</span>
          <h3 class="fk-world__card-title">${escapeHtml(path.title || '')}</h3>
          <span class="fk-world__gift-toggle" aria-hidden="true">
            <span class="fk-world__gift-open">Open</span>
            <span class="fk-world__gift-close">Close</span>
          </span>
        </summary>
        <div class="fk-world__gift-content">
          <p class="fk-world__gift-copy">${escapeHtml(path.gift_copy || '')}</p>
          <div class="fk-world__gift-facts">
            <div>
              <p class="fk-world__card-meta">Values</p>
              ${renderGiftList(path.values)}
            </div>
            <div>
              <p class="fk-world__card-meta">Signature traits</p>
              ${renderGiftList(path.traits)}
            </div>
          </div>
          <div class="fk-world__gift-buildings">
            <p class="fk-world__card-meta">Buildings</p>
            ${renderGiftList(path.buildings)}
          </div>
        </div>
      </details>
    `;
  }

  function renderStoryCard(story) {
    const url = story.url || story.atlas_url || '/pages/atlas';
    const image = cardMediaMarkup(story);

    return `
      <a class="fk-world__card fk-world__story-card" href="${escapeHtml(url)}">
        ${image}
        <span>
          <p class="fk-world__card-meta">Personal Entry — ${escapeHtml(story.id || '')}</p>
          <h3 class="fk-world__card-title">${escapeHtml(story.title || '')}</h3>
          ${story.collected_for ? `<p class="fk-world__card-note">Collected for ${escapeHtml(story.collected_for)}</p>` : ''}
          ${story.location ? `<p class="fk-world__card-note">${escapeHtml(story.location)}</p>` : ''}
          ${story.quote ? `<p class="fk-world__card-note">“${escapeHtml(story.quote)}”</p>` : ''}
        </span>
        <span class="fk-world__card-meta">Locate in the Atlas</span>
      </a>
    `;
  }

  async function initHome(root) {
    const data = await fetchJson(root.dataset.homeUrl);
    const hero = data.hero || {};
    const highlights = Array.isArray(data.highlights) ? data.highlights : [];

    const highlightSections = highlights.map((highlight, index) => {
      const align = highlight.align === 'left' || highlight.align === 'right' ? highlight.align : index % 2 === 0 ? 'left' : 'right';
      const theme = highlight.theme === 'dark' ? 'dark' : 'light';
      const cta = highlight.cta || 'Explore Collection';
      const secondaryCta = highlight.secondary_cta || '';
      const url = highlight.url || highlight.collection_url || '/pages/curations';
      const secondaryUrl = highlight.secondary_url || highlight.atlas_url || '';

      return `
        <article class="fk-world__highlight fk-world__highlight--${align} fk-world__highlight--${theme}">
          <div class="fk-world__highlight-media">
            ${mediaMarkup(highlight, highlight.title || 'Freezketch collection')}
          </div>
          <div class="fk-world__highlight-content">
            ${highlight.kicker ? `<p class="fk-world__kicker">${escapeHtml(highlight.kicker)}</p>` : ''}
            <h2 class="fk-world__hero-title">${escapeHtml(highlight.title || '')}</h2>
            ${highlight.intro ? `<p class="fk-world__text">${escapeHtml(highlight.intro)}</p>` : ''}
            <div class="fk-world__actions">
              ${button(url, cta)}
              ${button(secondaryUrl, secondaryCta, true)}
            </div>
          </div>
        </article>
      `;
    }).join('');

    root.innerHTML = `
      <div class="fk-world__hero">
        <div class="fk-world__hero-media">${heroMedia(hero)}</div>
        <div class="fk-world__hero-content">
          <p class="fk-world__kicker">${escapeHtml(hero.kicker || 'The Freezketch Atlas')}</p>
          <h1 class="fk-world__hero-title">${escapeHtml(hero.title || 'A collectible world of architecture')}</h1>
          <p class="fk-world__text" style="margin-left:auto;margin-right:auto;">${escapeHtml(hero.intro || '')}</p>
          <div class="fk-world__actions" style="justify-content:center;">
            ${button(hero.url || '/pages/atlas', hero.cta || 'Enter the Atlas')}
            ${button(hero.secondary_url || '/pages/collections', hero.secondary_cta || 'Explore Curations', true)}
          </div>
        </div>
      </div>

      ${highlightSections}
    `;
  }

  async function initCurations(root) {
    const data = await fetchJson(root.dataset.curationsUrl);
    const atlas = await getAtlas(root.dataset.atlasUrl);
    const byId = entryLookup(atlas);
    root.innerHTML = `
      <div class="fk-world__inner fk-world__split">
        <div>
          <p class="fk-world__kicker">${escapeHtml(data.kicker || 'Curations')}</p>
          <h1 class="fk-world__title">${escapeHtml(data.title || 'Curatorial lenses')}</h1>
          <p class="fk-world__text">${escapeHtml(data.intro || '')}</p>
        </div>
        <div class="fk-world__actions">${button('/pages/atlas', 'Enter the Atlas')}</div>
      </div>
      <div class="fk-world__band">
        <div class="fk-world__inner">
          <div class="fk-world__grid">
            ${(data.curations || []).map((curation) => `
              <a class="fk-world__card" href="${escapeHtml(curation.atlas_url || '/pages/atlas')}">
                <span>
                  <p class="fk-world__card-meta">${escapeHtml(curation.status || curation.count || '')}</p>
                  <h2 class="fk-world__card-title">${escapeHtml(curation.title)}</h2>
                  <p class="fk-world__card-note">${escapeHtml(curation.description || '')}</p>
                  <p class="fk-world__card-note">${(curation.featured_ids || []).map((id) => {
                    const entry = byId.get(id);
                    return escapeHtml(entry ? `${id} ${entry.title}` : id);
                  }).join('<br>')}</p>
                </span>
                <span class="fk-world__card-meta">Open Atlas filter</span>
              </a>
            `).join('')}
          </div>
        </div>
      </div>
    `;
  }

  async function initPaperRoutes(root) {
    const data = await fetchJson(root.dataset.routesUrl);
    const atlas = await getAtlas(root.dataset.atlasUrl);
    const byId = entryLookup(atlas);
    root.innerHTML = `
      <div class="fk-world__inner fk-world__split">
        <div>
          <p class="fk-world__kicker">${escapeHtml(data.kicker || 'Paper Routes')}</p>
          <h1 class="fk-world__title">${escapeHtml(data.title || 'Routes through the Atlas')}</h1>
          <p class="fk-world__text">${escapeHtml(data.intro || '')}</p>
        </div>
        <div class="fk-world__actions">${button('/pages/atlas', 'Enter the Atlas')}</div>
      </div>
      <div class="fk-world__band">
        <div class="fk-world__inner">
          ${(data.routes || []).map((route) => `
            <article class="fk-world__route" id="${escapeHtml(route.id)}">
              <div class="fk-world__feature">
                <div class="fk-world__media">${mediaMarkup(route, route.title)}</div>
                <div class="fk-world__panel">
                  <p class="fk-world__kicker">${escapeHtml(route.number || 'Paper Route')}</p>
                  <h2 class="fk-world__heading">${escapeHtml(route.title)}</h2>
                  <p class="fk-world__text">${escapeHtml(route.summary || '')}</p>
                  ${route.courier_note ? `<p class="fk-world__text">${escapeHtml(route.courier_note)}</p>` : ''}
                  <div class="fk-world__actions">${button(route.atlas_url, 'View this route on the Atlas')}</div>
                </div>
              </div>
              <div class="fk-world__sequence">
                ${(route.entries || []).map((id) => {
                  const entry = byId.get(id);
                  const title = entry?.title || id;
                  const meta = [entry?.city, entry?.country, entry?.architect].filter(Boolean).join(' / ');
                  return `
                    <a class="fk-world__sequence-item" href="${escapeHtml(entry?.product_url || route.atlas_url || '/pages/atlas')}">
                      <span class="fk-world__fk">${escapeHtml(id)}</span>
                      <span>
                        <strong>${escapeHtml(title)}</strong>
                        ${meta ? `<br><span class="fk-world__card-note">${escapeHtml(meta)}</span>` : ''}
                      </span>
                    </a>
                  `;
                }).join('')}
              </div>
            </article>
          `).join('')}
        </div>
      </div>
    `;
  }

  async function initGifting(root) {
    const data = await fetchJson(root.dataset.giftingUrl);
    root.innerHTML = `
      <div class="fk-world__inner fk-world__split">
        <div>
          <p class="fk-world__kicker">${escapeHtml(data.kicker || 'Gifting')}</p>
          <h1 class="fk-world__title">${escapeHtml(data.title || 'Gift an Atlas Entry')}</h1>
          <p class="fk-world__text">${escapeHtml(data.intro || '')}</p>
          <div class="fk-world__actions">
            ${button(data.cta_url, data.cta || 'Gift an Entry')}
            ${button(data.finder_url, data.finder_cta || 'Find an Entry', true)}
          </div>
        </div>
        <div class="fk-world__media">${mediaMarkup(data, data.title || 'Gift an Atlas Entry')}</div>
      </div>
      <div class="fk-world__band">
        <div class="fk-world__inner">
          <p class="fk-world__kicker">Gift paths</p>
          <h2 class="fk-world__heading">Begin with a way of seeing</h2>
          <div class="fk-world__gift-grid">${(data.paths || []).map(renderGiftPath).join('')}</div>
        </div>
      </div>
    `;
  }

  function boot(selector, fn) {
    document.querySelectorAll(selector).forEach((root) => {
      fn(root).catch(() => {
        root.innerHTML = '<div class="fk-world__error">This Freezketch section could not be loaded.</div>';
      });
    });
  }

  boot('[data-fk-home]', initHome);
  boot('[data-fk-curations]', initCurations);
  boot('[data-fk-paper-routes]', initPaperRoutes);
  boot('[data-fk-gifting]', initGifting);
})();
